import * as React from 'react';
import { useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, FormControl, FormControlLabel, FormHelperText, Grid, IconButton, LinearProgress, MenuItem, Radio, RadioGroup, Select, Typography, Paper, Button } from '@mui/material';
import useAuth from "../../context/AuthProvider";
import { Form, Formik } from 'formik';
import { FMTextField } from '../Elements/input';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { apiClient } from '../../helper/api';
import { PartnersFeesT_VS, PartnersFeesT_VS_bulkAdd, PartnersFeesTItemType } from '../../models/PartnersFeesTModel';

const selectFieldSx = {
  width: '100%',
  '& .MuiSelect-select': { padding: '14.5px 16px' },
  '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #e2e8f0 !important', borderRadius: '8px' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2 !important' },
};
const textFieldSx = {
  '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #e2e8f0 !important', borderRadius: '8px !important' },
  '& .MuiInputBase-input': { padding: '5px 14px !important' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2 !important' },
};

interface Iprops {
  onClose: () => void,
  isOpen: number,
  onSubmit: (item: PartnersFeesTItemType, addMissingOnly?: boolean) => void
  initItem: PartnersFeesTItemType
  selItem: PartnersFeesTItemType
}

const toISODate = (input?: string) => {
  if (!input) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(input)) return input;
  const [mm, dd, yyyy] = input.split('/');
  return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
};

export const AddEditPartnersFeesT: React.FC<Iprops> = ({ onClose, isOpen, initItem, onSubmit, selItem }) => {
  const { user } = useAuth();
  const token = user?.token;

  const [loading, setloading] = React.useState(false);

  const initVal = selItem?.partnersFeesTId ? selItem : initItem;
  const initFormVal = useMemo(() => ({
    ...initVal,
    dateAsOf: toISODate(initVal.dateAsOf as string) || (new Date()).toISOString().split("T")[0]
  }), [initVal])

  const initialPartnerdata = { partnersId: 0, customerName: "" };
  const [customerList, setpartnerData] = React.useState([initialPartnerdata]);
  const initState = { stateId: 0, stateName: "", postalAbbv: '' };
  const [stateList, setStateList] = useState([initState]);

  const isBulkAdd = isOpen === 2;
  const [bulkAddMode, setBulkAddMode] = useState<string>('missing');

  const fetchCustomer = () => {
    apiClient('/Partners/getPartners', {
      method: "GET",
      headers: new Headers({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      })
    }).then((responseJson) => {
      if (!responseJson) return;
      setpartnerData(responseJson.data);
    });
  };
  const fetchStateData = () => {
    apiClient("/States/getStates", {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
    }).then((responseJson) => {
      if (!responseJson) return;
      setStateList(responseJson.data);
    });
  };

  React.useEffect(() => {
    fetchCustomer();
    fetchStateData();
  }, []);

  const dialogTitle = !isBulkAdd
    ? (selItem.partnersFeesTId === 0 ? 'New PartnersFeesT' : 'Edit PartnersFeesT')
    : 'Bulk Add PartnersFeesT';

  return (
    <Dialog
      open={isOpen > 0}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          width: '90%',
          maxWidth: 900,
          minHeight: '50vh',
          maxHeight: '90vh',
          borderRadius: 2,
          boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
        },
      }}
      sx={{ '& .MuiDialog-paper': { overflow: 'hidden' } }}
    >
      {loading && <LinearProgress sx={{ flexShrink: 0 }} />}
      <DialogTitle
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          py: 2,
          px: 3,
          background: 'linear-gradient(135deg, #0d4a63 0%, #15607a 100%)',
          color: '#fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ReceiptLongOutlinedIcon sx={{ fontSize: 28, opacity: 0.95 }} />
          <Typography component="span" variant="h6" sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
            {dialogTitle}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' } }}
          aria-label="Close dialog"
        >
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>
      <Formik
        initialValues={initFormVal}
        enableReinitialize={true}
        validateOnChange={true}
        validationSchema={!isBulkAdd ? PartnersFeesT_VS : PartnersFeesT_VS_bulkAdd}
        onSubmit={async (data, { setSubmitting }) => {
          onSubmit(data, bulkAddMode === 'missing');
          setloading(false);
          setSubmitting(false);
        }}
      >
        {({ values, isSubmitting, setFieldValue, errors }) => (
          <Form className='main-form overflow-auto'>
            <DialogContent sx={{ p: '0px' , '& > .MuiGrid-root': { px: { xs: 2, sm: 3 }, py: 3 } }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', display: 'block', mb: 1 }}>
                    Fee details
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider', backgroundColor: 'grey.50' }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={!isBulkAdd ? 6 : 12} md={!isBulkAdd ? 6 : 12}>
                        <FormControl error={!!errors.partnerId} sx={{ width: '100%' }}>
                          <FormHelperText className='formtexthelper-heading'>Client <span style={{ color: 'red' }}>*</span></FormHelperText>
                          <Select
                            value={values.partnerId}
                            id="partner-select"
                            label={null}
                            onChange={(e) => setFieldValue("partnerId", Number(e.target.value))}
                            name="partnerId"
                            IconComponent={ExpandMoreIcon}
                            displayEmpty
                            sx={selectFieldSx}
                          >
                            <MenuItem key={0} value={0}>Select Client</MenuItem>
                            {customerList.map((customer, key) => (
                              <MenuItem key={key} value={customer.partnersId}>{customer.customerName}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText error>{typeof errors.partnerId === 'string' ? errors.partnerId : ''}</FormHelperText>
                        </FormControl>
                      </Grid>
                      {!isBulkAdd && (
                        <Grid item xs={12} sm={6} md={6}>
                          <FormControl error={!!errors.stateId} sx={{ width: '100%' }}>
                            <FormHelperText className='formtexthelper-heading'>States <span style={{ color: 'red' }}>*</span></FormHelperText>
                            <Select
                              value={values.stateId}
                              id="state-select"
                              label={null}
                              onChange={(e) => setFieldValue("stateId", Number(e.target.value))}
                              name="stateId"
                              IconComponent={ExpandMoreIcon}
                              displayEmpty
                              sx={selectFieldSx}
                            >
                              <MenuItem key={0} value={0}>Select State</MenuItem>
                              {stateList.map((state, key) => (
                                <MenuItem key={key} value={state.stateId}>{state.stateName}</MenuItem>
                              ))}
                            </Select>
                            <FormHelperText error>{typeof errors.stateId === 'string' ? errors.stateId : ''}</FormHelperText>
                          </FormControl>
                        </Grid>
                      )}

                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormHelperText className='formtexthelper-heading'>Zero Fee</FormHelperText>
                          <FMTextField name="zeroFee" margin="dense" label={null} type="number" fullWidth variant="outlined" sx={{...textFieldSx, input: { textAlign: 'right' }}} />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormHelperText className='formtexthelper-heading'>Premium Fee</FormHelperText>
                          <FMTextField name="premiumFee" margin="dense" label={null} type="number" fullWidth variant="outlined" sx={{...textFieldSx, input: { textAlign: 'right' }}} />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormHelperText className='formtexthelper-heading'>Policy Fee</FormHelperText>
                          <FMTextField name="policyFee" margin="dense" label={null} type="text" fullWidth variant="outlined" sx={{...textFieldSx, input: { textAlign: 'right' }}} />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormHelperText className='formtexthelper-heading'>Loyds Fee</FormHelperText>
                          <FMTextField name="loydsFee" margin="dense" label={null} type="text" fullWidth variant="outlined" sx={{...textFieldSx, input: { textAlign: 'right' }}} />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormHelperText className='formtexthelper-heading'>Binder Fee</FormHelperText>
                          <FMTextField name="binderFee" margin="dense" label={null} type="text" fullWidth variant="outlined" sx={{...textFieldSx, input: { textAlign: 'right' }}} />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormHelperText className='formtexthelper-heading'>Endorsement Fee</FormHelperText>
                          <FMTextField name="endorsementFee" margin="dense" label={null} type="text" fullWidth variant="outlined" sx={{...textFieldSx, input: { textAlign: 'right' }}} />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormHelperText className='formtexthelper-heading'>LargeReport Fee</FormHelperText>
                          <FMTextField name="largeReportFee" margin="dense" label={null} type="text" fullWidth variant="outlined" sx={{...textFieldSx, input: { textAlign: 'right' }}} />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl sx={{ width: '100%' }}>
                          <FormHelperText className='formtexthelper-heading'>Diligent Search Fee</FormHelperText>
                          <FMTextField name="diligentSearchFee" margin="dense" label={null} type="text" fullWidth variant="outlined" sx={{...textFieldSx, input: { textAlign: 'right' }}} />
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl error={!!errors.dateAsOf} sx={{ width: '100%' }}>
                          <FormHelperText className='formtexthelper-heading'>Date As Of</FormHelperText>
                          <FMTextField
                            name="dateAsOf"
                            margin="dense"
                            label={null}
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            variant="outlined"
                            sx={textFieldSx}
                          />
                          <FormHelperText error>{errors.dateAsOf}</FormHelperText>
                        </FormControl>
                      </Grid>

                      {isBulkAdd && (
                        <Grid item xs={12}>
                          <FormControl component="fieldset">
                            <FormHelperText className='formtexthelper-heading' sx={{ mb: 0.5 }}>Bulk add mode</FormHelperText>
                            <RadioGroup
                              row
                              value={bulkAddMode}
                              onChange={(e) => setBulkAddMode(e.target.value)}
                            >
                              <FormControlLabel value="missing" control={<Radio color="primary" />} label="Add fees only for missing states" />
                              <FormControlLabel value="all" control={<Radio color="primary" />} label="Add new fees for all states" />
                            </RadioGroup>
                          </FormControl>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions
              sx={{
                px: 3,
                py: 2,
                gap: 1.5,
                borderTop: 1,
                borderColor: 'divider',
                backgroundColor: 'grey.50',
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="outlined"
                size="medium"
                onClick={onClose}
                startIcon={<CloseOutlinedIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: '#e2e8f0',
                  color: '#475569',
                  '&:hover': {
                    borderColor: '#2099d2',
                    backgroundColor: '#2099d214',
                    color: '#2099d2',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="medium"
                type="submit"
                disabled={isSubmitting}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  backgroundColor: '#2099d2',
                  '&:hover': { backgroundColor: '#176990' },
                  px: 2.5,
                }}
              >
                Submit
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};
